import { laptopMax, mobileXXLargeMax, tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import { PRIME_BG_COLOR_MAP, PRIME_COLOR_MAP } from '@propertypal/shared/src/constants/prime';
import platform from '@propertypal/shared/src/utils/platform';
import styled from 'styled-components';

export const Container = styled.div`
  background-color: ${({ theme }) => theme.white};
  max-width: 1240px;
  margin: 0 auto 15px;
  padding: 0 20px;
`;

export const TiersGrid = styled.div`
  display: flex;
  column-gap: 35px;
  row-gap: 40px;
  align-items: stretch;
  justify-content: center;
  width: 100%;

  @media (${laptopMax}) {
    flex-direction: column;
    align-items: center;
  }
`;

export const TabBar = styled.div`
  display: flex;
  width: fit-content;
  background: ${({ theme }) => theme.background};
  border-radius: 999px;
  margin: 0 auto 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

export const Tab = styled.button`
  width: 150px;
  background: transparent;
  color: ${({ theme }) => theme.textDark};
  padding: 15px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &[aria-selected='true'] {
    background: ${({ theme }) => theme.textDark};
    color: ${({ theme }) => theme.white};
    font-weight: bold;
  }
`;

export const TableWrap = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;

  @media (${tabletMax}) {
    display: none;
  }
`;

export const ComparisonTable = styled.table`
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 940px;
  margin: 30px 0;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  border-radius: 12px;

  thead th {
    background: ${({ theme }) => theme.white};
    vertical-align: bottom;
  }

  tfoot th {
    background: ${({ theme }) => theme.white};
  }

  tbody tr:first-child th,
  tbody tr:first-child td {
    border-top: 1px solid ${({ theme }) => theme.backgroundLightest};
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: 1px solid ${({ theme }) => theme.backgroundLightest};
  }

  tbody tr th:first-child,
  tbody tr td:first-child {
    border-left: 1px solid ${({ theme }) => theme.backgroundLightest};
  }

  tbody tr th:last-child,
  tbody tr td:last-child {
    border-right: 1px solid ${({ theme }) => theme.backgroundLightest};
  }

  tbody tr:nth-child(odd) th,
  tbody tr:nth-child(odd) td {
    background: ${({ theme }) => theme.white};
  }

  tbody tr:nth-child(even) th,
  tbody tr:nth-child(even) td {
    background: ${({ theme }) => theme.backgroundLightest};
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    background: rgba(248, 237, 218, 0.5);
  }
`;

export const Ruler = styled.div`
  width: 220px;
  height: 4px;
  margin: 15px auto -16px;

  @media (${tabletMax}) {
    width: 100%;
    max-width: 220px;
    margin-bottom: 0;
  }
`;

export const BoxShadow = styled.div<{
  $left?: number;
  $width?: number;
  $top?: number;
  $height?: number;
  $visible?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  pointer-events: none;
  z-index: 2;
  opacity: 0;
`;

export const ColumnOverlay = styled.div`
  position: absolute;
  z-index: 3;
`;

export const FeatureLabel = styled.th<{ $border?: boolean }>`
  position: relative;
  z-index: 1;
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  color: ${({ theme }) => theme.textDark};
  text-align: left;
  vertical-align: bottom;
  font-size: 14px;
  font-weight: 700;
  padding: 16px;
  border-right: ${({ $border, theme }) => ($border ? `1px solid ${theme.backgroundLighter}` : 'none')};
`;

export const HighlightCell = styled.th<{ $highlight?: boolean }>`
  position: relative;
  z-index: 1;
  text-align: center;
  vertical-align: bottom;
  padding: 20px 14px 16px;
`;

export const DiscountBubble = styled.div`
  position: absolute;
  top: -10px;
  right: 10px;
  color: ${({ theme }) => theme.white};
  border-radius: 999px;
  font-size: 18px;
  font-weight: 800;
  padding: 4px 8px;
  line-height: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 66px;
  height: 66px;
  transform: rotate(15deg);

  span {
    color: rgba(255, 255, 255, 0.8);
  }
`;

export const RecommendedTag = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${PRIME_BG_COLOR_MAP.SILVER};
  color: ${PRIME_COLOR_MAP.SILVER};
  border-radius: 8px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 4px 10px;
  white-space: nowrap;
`;

export const TierHeading = styled.div`
  font-size: 22px;
  font-family: ${platform.headingFont};
  margin-bottom: 8px;
`;

export const ViewExampleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.textMid};
  text-decoration: underline;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 10px;
`;

export const PriceWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

export const PricePrevious = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.backgroundMid};
  text-decoration: line-through;
  font-weight: normal;
  margin-bottom: -10px;
`;

export const PriceCurrent = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.textDark};
  font-family: ${platform.headingFont};

  span {
    font-size: 18px;
  }
`;

export const FeatureValue = styled.td<{ $highlight?: boolean }>`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 14px 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.textDark};
`;

export const FeatureCellContent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const PreviewImage = styled.img`
  display: block;
  width: 80%;
  height: auto;
  margin: 0 auto 30px;
  max-width: 600px;
`;

export const PreviewSwiperWrap = styled.div`
  .swiper-button-next,
  .swiper-button-prev {
    color: ${({ theme }) => theme.textDark};
  }
  .swiper-wrapper {
    display: flex;
    align-items: center;
  }

  @media (${mobileXXLargeMax}) {
    .swiper-button-next,
    .swiper-button-prev {
      width: 30px;
      height: 30px;
    }
  }
`;

export const UpgradeCell = styled(FeatureValue)`
  padding: 18px 12px;
`;

export const UpgradeButton = styled.a<{ $bg: string; $filled?: boolean }>`
  display: inline-block;
  border: 2px solid ${({ $bg }) => $bg};
  background: ${({ $filled, $bg }) => ($filled ? $bg : 'transparent')};
  color: ${({ theme, $filled, $bg }) => ($filled ? theme.white : $bg)};
  font-size: 14px;
  font-weight: 700;
  width: 220px;
  padding: 12px 24px;
  border-radius: 999px;
  text-decoration: none;
  text-align: center;
  transition: opacity 0.2s ease;
  cursor: pointer;
  min-width: 140px;

  @media (${laptopMax}) {
    min-width: 120px;
  }

  &:hover {
    opacity: 0.9;
  }
`;

export const InfoButton = styled.button`
  cursor: pointer;
  margin-left: 5px;
`;

export const MobileTierSwiper = styled.div`
  display: none;

  @media (${tabletMax}) {
    display: block;
    margin: 24px -20px 35px;
    overflow: hidden;
    padding: 0 20px 8px;

    .swiper {
      overflow: visible;
    }
  }
`;

export const MobileTierSwiperPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${({ theme }) => theme.textMid};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 18px;
  text-transform: uppercase;
`;

export const MobileTierCard = styled.div<{ $highlight?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 515px;
  background: ${({ theme }) => theme.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.1);
  padding: 24px 18px 20px;
  border: ${({ $highlight }) => ($highlight ? `2px solid ${PRIME_COLOR_MAP.SILVER}` : 'none')};

  ${UpgradeButton} {
    align-self: center;
    margin-top: auto;
  }
`;

export const MobileTierHeader = styled.div`
  text-align: center;
  padding: 10px 0;
`;

export const MobileFeatureRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 15px;
  color: ${({ $disabled, theme }) => ($disabled ? theme.backgroundMid : theme.textDark)};
  text-align: left;
  font-size: 14px;
  gap: 10px;
  max-width: 240px;
`;
